import { createElement } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { SurveySelectComponent } from './SurveySelectComponent';

/**
 * Writes attributes onto this node at its own position.
 *
 * The `updateAttributes` helper handed to the node view no-ops for this node,
 * so picking a survey left the document on the previous id — or on none at all
 * when the node came from a hydrated `{{survey.link}}` — while the pill showed
 * the newly picked label. The serialised body then still read as an unfilled
 * placeholder and the send was blocked with "Please select a survey" for a
 * survey that was plainly selected (ENG-3826).
 *
 * Exported for tests.
 */
export const writeSurveyAttributes = (props, attrs) => {
  const pos = typeof props?.getPos === 'function' ? props.getPos() : null;
  const view = props?.editor?.view;

  if (view && typeof pos === 'number') {
    view.dispatch(
      view.state.tr.setNodeMarkup(pos, undefined, {
        ...props.node?.attrs,
        ...attrs,
      })
    );
    return;
  }

  props?.updateAttributes?.(attrs);
};

// Custom TipTap node for survey selection
export const SurveyNode = Node.create({
  name: 'surveySelect',

  group: 'inline',

  inline: true,

  atom: true,

  addOptions() {
    return {
      onSurveySelect: null,
      onSurveyRemove: null,
    };
  },

  addAttributes() {
    return {
      surveyId: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-survey-select]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-survey-select': '',
        class: 'survey-node-container',
      }),
    ];
  },

  // Emit the `{{survey.link}}` token for getText()/clipboard so duplicate-insert
  // checks (EditorCustomVariables.isVariableUsed) keep working after the survey
  // text is hydrated into this node.
  renderText() {
    return '{{survey.link}}';
  },

  addNodeView() {
    return ReactNodeViewRenderer(
      (props) =>
        createElement(SurveySelectComponent, {
          ...props,
          updateAttributes: (attrs) => writeSurveyAttributes(props, attrs),
        }),
      {
        onSurveySelect: this.options.onSurveySelect,
        onSurveyRemove: this.options.onSurveyRemove,
      }
    );
  },

  addCommands() {
    return {
      insertSurvey:
        (attributes) =>
        ({ commands, tr }) => {
          // Check if there's space at the cursor position
          const $from = tr.selection.$from;
          const after = $from.nodeAfter;

          // If there's content after cursor and no space, add a space or new line
          if (after && after.text && !after.text.startsWith(' ')) {
            commands.insertContent(' ');
          }

          return commands.insertContent({
            type: this.name,
            attrs: attributes,
          });
        },
      deleteSurvey:
        () =>
        ({ state, dispatch }) => {
          const { selection } = state;
          const { $anchor } = selection;

          // Try to find survey node before or after cursor
          let nodeToDelete = null;
          let pos = null;

          if (
            $anchor.nodeBefore &&
            $anchor.nodeBefore.type.name === this.name
          ) {
            nodeToDelete = $anchor.nodeBefore;
            pos = $anchor.pos - nodeToDelete.nodeSize;
          } else if (
            $anchor.nodeAfter &&
            $anchor.nodeAfter.type.name === this.name
          ) {
            nodeToDelete = $anchor.nodeAfter;
            pos = $anchor.pos;
          }

          if (nodeToDelete && pos !== null) {
            // Call onSurveyRemove if callback exists
            if (this.options.onSurveyRemove && nodeToDelete.attrs?.surveyId) {
              const surveyId = nodeToDelete.attrs.surveyId;
              this.options.onSurveyRemove({ value: surveyId, surveyId });
            }

            if (dispatch) {
              const tr = state.tr.delete(pos, pos + nodeToDelete.nodeSize);
              dispatch(tr);
            }
            return true;
          }

          return false;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // Handle backspace to delete the node
      Backspace: () => {
        const { state } = this.editor;
        const { selection } = state;
        const { $anchor } = selection;

        // Check if we're at the start of the node or the node is selected
        if ($anchor.nodeBefore && $anchor.nodeBefore.type.name === this.name) {
          return this.editor.commands.deleteSurvey();
        }

        return false;
      },
      // Handle delete key as well
      Delete: () => {
        const { state } = this.editor;
        const { selection } = state;
        const { $anchor } = selection;

        // Check if the node after cursor is a survey node
        if ($anchor.nodeAfter && $anchor.nodeAfter.type.name === this.name) {
          return this.editor.commands.deleteSurvey();
        }

        return false;
      },
    };
  },
});
